import React, { useState, useEffect } from 'react';
import { Cloud, Shield, Search, CheckCircle, AlertTriangle, ChevronRight, ChevronLeft, ArrowRight, Loader, Settings } from 'lucide-react';
import { apiClient } from '../../services/api/client';
import { extractApiError } from '../../utils/error';
import {
  PROVIDER_CREDENTIAL_FORMS, ProviderCredentialFields,
  buildCreateCredentials, missingCredFields, seedCredForm,
} from '../Common/ProviderCredentialForm';

interface OnboardingWizardProps {
  onComplete: () => void;
}

type Provider = 'aws' | 'azure' | 'gcp';
type ScanType = 'quick' | 'full';

// FOUR STATUSES ARE TERMINAL AND THE POLL LOOP KNEW ABOUT TWO.
// `if (scanStatus === 'completed' || scanStatus === 'failed') return;` was the
// stop condition, so a scan that ended as `partial` or `cancelled` left the
// wizard polling every 3s forever behind a "Scanning..." bar that never moves.
// `cancelled` has always been reachable; `partial` became the COMMON case on
// 2026-08-10 when the agentless scanner started writing it for a scan that ran
// but inspected no disk — live, 185 rows against 1 `completed`.
//
// Enumerated once, here, rather than repeated at each of the three sites that
// need it. That is the actual defect: the same two-element list was written out
// by hand in the poll guard, the progress-bar condition and the summary text,
// so adding a status meant remembering all three.
const TERMINAL_SCAN_STATUSES = ['completed', 'partial', 'failed', 'cancelled'];
const isTerminalScan = (s: string) => TERMINAL_SCAN_STATUSES.includes(s);

const STEP_LABELS = ['Welcome', 'Connect Cloud', 'Configure Scan', 'Run First Scan', 'Results'];
const STEP_ICONS = [Shield, Cloud, Settings, Search, CheckCircle];

// THIS FILE USED TO CARRY ITS OWN THREE-PROVIDER FIELD LIST, AND IT COULD NOT
// CONNECT ANYTHING. Measured against the live gateway 2026-08-09: the single-
// account path POSTed `{provider, name, credentials}` with **no external_id**,
// so create answered **HTTP 400 `external_id required for provider '<p>'`** for
// aws, azure AND gcp. The first-run wizard — the first thing a new tenant sees
// — had been unable to onboard any cloud account since that requirement went
// catalog-wide on 2026-08-01, and nothing compared this surface to Settings.
//
// The fields were independently wrong: AWS asked for a role ARN with no
// External ID (create refuses that pair as a confused-deputy risk) and never
// offered access keys; Azure asked for a tenant id with no client id or
// secret, so the probe had nothing to authenticate with; GCP asked for the
// service-account EMAIL, which authenticates nothing at all — the key file is
// what the probe needs.
//
// The field definitions now come from components/Common/ProviderCredentialForm,
// the same table Settings renders, checked against provider_catalog.py by arm
// P7 of check_connector_provider_parity.py.
const PROVIDER_LABELS: Record<Provider, string> = {
  aws: 'Amazon Web Services',
  azure: 'Microsoft Azure',
  gcp: 'Google Cloud Platform',
};

// ── Step 0: Welcome ──────────────────────────────────────────────────────────
const StepWelcome: React.FC = () => (
  <div className="flex flex-col items-center text-center max-w-xl mx-auto">
    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-teal-600 flex items-center justify-center mb-6 shadow-2xl shadow-indigo-500/20">
      <Shield className="w-10 h-10 text-white" />
    </div>
    <h2 className="text-3xl font-bold text-gray-200 mb-3">Welcome to CyberShield</h2>
    <p className="text-gray-400 text-lg mb-8 leading-relaxed">
      Your unified cloud security platform. Connect your cloud accounts, run your first security scan,
      and get instant visibility into misconfigurations, vulnerabilities, and compliance gaps.
    </p>
    <div className="grid grid-cols-2 gap-4 w-full text-left">
      {[
        { icon: Cloud, title: 'Multi-Cloud Coverage', desc: 'AWS, Azure, and GCP in one view' },
        { icon: Shield, title: 'CSPM & Compliance', desc: 'CIS, SOC 2, PCI-DSS, HIPAA, NIST' },
        { icon: Search, title: 'Agentless Scanning', desc: 'Read-only access, no agents needed' },
        { icon: CheckCircle, title: 'Attack Path Analysis', desc: 'Graph-based risk visualization' },
      ].map(({ icon: Icon, title, desc }) => (
        <div key={title} className="flex items-start gap-3 p-4 bg-wiz-card border border-wiz-border rounded-xl">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-indigo-400" />
          </div>
          <div>
            <p className="text-gray-200 text-sm font-semibold">{title}</p>
            <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── Step 1: Connect Cloud ────────────────────────────────────────────────────
// R220 — Wiz parity: bulk-onboard Azure Management Groups and GCP Org/Folders
// in addition to AWS Organizations. BE endpoints already exist:
//   POST /api/v1/connectors/discover-mg + /onboard-mg (org_onboarding.py:141, 160)
//   POST /api/v1/connectors/discover-gcp-org + /onboard-gcp-org (l.242, l.265)
type ConnectMode = 'single' | 'aws_org' | 'azure_mg' | 'gcp_org';

interface OrgAccount {
  id: string;
  name?: string;
  email?: string;
  status?: string;
  ou_path?: string;
}

const StepConnectCloud: React.FC<{
  accountId: string | null;
  onAccountCreated: (id: string) => void;
  onProviderChange?: (provider: Provider) => void;
}> = ({ accountId, onAccountCreated, onProviderChange }) => {
  const [provider, setProvider] = useState<Provider>('aws');
  const [mode, setMode] = useState<ConnectMode>('single');
  const [form, setForm] = useState<Record<string, string>>(() => seedCredForm(PROVIDER_CREDENTIAL_FORMS.aws));
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  //: Set when create SUCCEEDED but the credential probe then failed. Distinct
  //  from `error`, which means nothing was created — the connector exists here
  //  and the operator can continue, but the first scan will find nothing and
  //  "0 findings" is the most reassuring possible way to fail.
  const [verifyError, setVerifyError] = useState('');
  // AWS Organizations bulk-onboarding state
  const [orgRoleArn, setOrgRoleArn] = useState('');
  const [orgExternalId, setOrgExternalId] = useState('');
  const [memberRoleName, setMemberRoleName] = useState('OrganizationAccountAccessRole');
  const [orgAccounts, setOrgAccounts] = useState<OrgAccount[]>([]);
  const [orgSelected, setOrgSelected] = useState<Set<string>>(new Set());
  const [orgDiscovering, setOrgDiscovering] = useState(false);
  const [orgOnboarding, setOrgOnboarding] = useState(false);
  const [orgSummary, setOrgSummary] = useState<{ succeeded: number; failed: number } | null>(null);
  // R220 — Azure MG bulk-onboard
  const [azMgRoot, setAzMgRoot] = useState('');
  const [azClientId, setAzClientId] = useState('');
  const [azClientSecret, setAzClientSecret] = useState('');
  const [azTenantId, setAzTenantId] = useState('');
  // R220 — GCP Org bulk-onboard
  const [gcpOrgId, setGcpOrgId] = useState('');
  const [gcpServiceAccountJson, setGcpServiceAccountJson] = useState('');
  const [gcpFolderFilter, setGcpFolderFilter] = useState('');

  const handleDiscoverAzureMG = async () => {
    if (!azMgRoot.trim() || !azClientId.trim() || !azClientSecret.trim() || !azTenantId.trim()) {
      setError('Management group ID, client ID, client secret, and tenant ID are required');
      return;
    }
    setOrgDiscovering(true);
    setError('');
    try {
      const { data, error, status } = await apiClient.post<any>('/api/v1/connectors/discover-mg', {
        management_group_id: azMgRoot.trim(),
        client_id: azClientId.trim(),
        client_secret: azClientSecret,
        tenant_id: azTenantId.trim(),
      });
      if (error) throw new Error(extractApiError({}, `${error} (${status})`));
      const subs: OrgAccount[] = (data as any)?.subscriptions || (data as any)?.accounts || [];
      setOrgAccounts(subs);
      setOrgSelected(new Set(subs.map(s => s.id)));
    } catch (e: any) {
      setError(e.message || 'Azure MG discovery failed');
    } finally {
      setOrgDiscovering(false);
    }
  };

  const handleOnboardAzureMG = async () => {
    if (orgSelected.size === 0) { setError('Select at least one subscription'); return; }
    setOrgOnboarding(true);
    setError('');
    try {
      const { data, error, status } = await apiClient.post<any>('/api/v1/connectors/onboard-mg', {
        management_group_id: azMgRoot.trim(),
        client_id: azClientId.trim(),
        client_secret: azClientSecret,
        tenant_id: azTenantId.trim(),
        subscription_filter: Array.from(orgSelected),
      });
      if (error) throw new Error(extractApiError({}, `${error} (${status})`));
      const d: any = data || {};
      setOrgSummary({ succeeded: d.succeeded ?? d.onboarded ?? orgSelected.size, failed: d.failed ?? 0 });
      onAccountCreated((d.connectors && d.connectors[0]?.id) || 'azure-mg-bulk');
    } catch (e: any) {
      setError(e.message || 'Azure MG bulk onboard failed');
    } finally {
      setOrgOnboarding(false);
    }
  };

  const handleDiscoverGCPOrg = async () => {
    if (!gcpOrgId.trim() || !gcpServiceAccountJson.trim()) {
      setError('GCP organization ID and service-account JSON are required');
      return;
    }
    setOrgDiscovering(true);
    setError('');
    try {
      let saKey: any;
      try { saKey = JSON.parse(gcpServiceAccountJson); }
      catch { throw new Error('Service-account JSON is not valid JSON'); }
      const { data, error, status } = await apiClient.post<any>('/api/v1/connectors/discover-gcp-org', {
        organization_id: gcpOrgId.trim(),
        service_account_key: saKey,
        folder_filter: gcpFolderFilter ? gcpFolderFilter.split(',').map(s => s.trim()).filter(Boolean) : [],
      });
      if (error) throw new Error(extractApiError({}, `${error} (${status})`));
      const projects: OrgAccount[] = (data as any)?.projects || (data as any)?.accounts || [];
      setOrgAccounts(projects);
      setOrgSelected(new Set(projects.map(p => p.id)));
    } catch (e: any) {
      setError(e.message || 'GCP Org discovery failed');
    } finally {
      setOrgDiscovering(false);
    }
  };

  const handleOnboardGCPOrg = async () => {
    if (orgSelected.size === 0) { setError('Select at least one project'); return; }
    setOrgOnboarding(true);
    setError('');
    try {
      let saKey: any;
      try { saKey = JSON.parse(gcpServiceAccountJson); }
      catch { throw new Error('Service-account JSON is not valid JSON'); }
      const { data, error, status } = await apiClient.post<any>('/api/v1/connectors/onboard-gcp-org', {
        organization_id: gcpOrgId.trim(),
        service_account_key: saKey,
        project_filter: Array.from(orgSelected),
        folder_filter: gcpFolderFilter ? gcpFolderFilter.split(',').map(s => s.trim()).filter(Boolean) : [],
      });
      if (error) throw new Error(extractApiError({}, `${error} (${status})`));
      const d: any = data || {};
      setOrgSummary({ succeeded: d.succeeded ?? d.onboarded ?? orgSelected.size, failed: d.failed ?? 0 });
      onAccountCreated((d.connectors && d.connectors[0]?.id) || 'gcp-org-bulk');
    } catch (e: any) {
      setError(e.message || 'GCP Org bulk onboard failed');
    } finally {
      setOrgOnboarding(false);
    }
  };

  const handleConnect = async () => {
    const credForm = PROVIDER_CREDENTIAL_FORMS[provider];
    const missing = missingCredFields(credForm, form);
    if (missing.length) {
      setError(`Required: ${missing.join(', ')}`);
      return;
    }
    const { externalId, credentials, topLevel } = buildCreateCredentials(credForm, form);
    if (!externalId) {
      // Belt and braces: create refuses a blank external_id with a 400, because
      // cloud_accounts carries a CHECK on it. That 400 is exactly what this
      // wizard used to hit on every attempt.
      setError(`${credForm.fields[0].label} is required`);
      return;
    }
    setConnecting(true);
    setError('');
    try {
      const { data, error, status } = await apiClient.post<any>('/api/v1/cloud-accounts', {
        provider,
        name: externalId,
        external_id: externalId,
        account_id: externalId,
        credentials,
        ...topLevel,
        cspm_enabled: true,
        ciem_enabled: true,
      });
      if (error) {
        throw new Error(extractApiError(data || {}, `${error} (${status})`));
      }
      const id = (data as any)?.id;
      if (!id) {
        // No id means nothing was persisted. Reporting success here is how the
        // Settings grid used to leave a "pending" row that the next poll
        // silently deleted; the previous version of this handler defaulted to
        // the literal string 'demo' and carried on to the scan step.
        throw new Error('The connector service did not return a connector id.');
      }
      onAccountCreated(id);
      // Verify the credentials for real, the same probe Settings runs after
      // create. Failure is surfaced rather than swallowed — a connector that
      // cannot authenticate will produce an empty first scan, and "0 findings"
      // is the most reassuring possible way to fail.
      apiClient.post<any>(`/api/v1/cloud-accounts/${id}/test`)
        .then(({ data: testData }) => {
          if (!testData?.success) {
            setVerifyError(testData?.message || 'Credential verification failed.');
          }
        })
        .catch(() => setVerifyError('Could not reach the connector service to verify credentials.'));
    } catch (e: any) {
      setError(e.message || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleDiscoverOrg = async () => {
    if (!orgRoleArn.trim()) {
      setError('Master/management account role ARN is required');
      return;
    }
    setOrgDiscovering(true);
    setError('');
    try {
      const { data, error, status } = await apiClient.post<any>('/api/v1/cloud-accounts/discover-org', {
        org_role_arn: orgRoleArn,
        external_id: orgExternalId || null,
        include_tags: false,
      });
      if (error) {
        throw new Error(extractApiError({}, `${error} (${status})`));
      }
      const accounts: OrgAccount[] = (data as any)?.accounts || [];
      setOrgAccounts(accounts);
      const initial = new Set<string>(accounts.filter(a => a.status === 'ACTIVE').map(a => a.id));
      setOrgSelected(initial);
    } catch (e: any) {
      setError(e.message || 'Org discovery failed');
    } finally {
      setOrgDiscovering(false);
    }
  };

  const handleOnboardOrg = async () => {
    if (orgSelected.size === 0) {
      setError('Select at least one account to onboard');
      return;
    }
    setOrgOnboarding(true);
    setError('');
    try {
      const { data, error, status } = await apiClient.post<any>('/api/v1/cloud-accounts/onboard-org', {
        org_role_arn: orgRoleArn,
        external_id: orgExternalId || null,
        member_role_name: memberRoleName,
        account_filter: Array.from(orgSelected),
        regions: [],
        cspm_enabled: true,
      });
      if (error) {
        throw new Error(extractApiError({}, `${error} (${status})`));
      }
      setOrgSummary((data as any)?.summary || { succeeded: 0, failed: 0 });
      const firstId = ((data as any)?.onboarded || [])[0]?.connector_id;
      onAccountCreated(firstId || `aws-org:${orgSelected.size}`);
    } catch (e: any) {
      setError(e.message || 'Bulk onboard failed');
    } finally {
      setOrgOnboarding(false);
    }
  };

  const toggleOrgAccount = (id: string) => {
    setOrgSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (accountId) {
    return (
      <div className="max-w-xl mx-auto text-center">
        <div className="w-16 h-16 rounded-full bg-green-500/10 border-2 border-green-500 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-200 mb-2">
          {mode === 'aws_org' ? 'AWS Organization onboarded'
           : mode === 'azure_mg' ? 'Azure Management Group onboarded'
           : mode === 'gcp_org' ? 'GCP Organization onboarded'
           : 'Cloud account connected'}
        </h2>
        <p className="text-gray-400">
          {(mode === 'aws_org' || mode === 'azure_mg' || mode === 'gcp_org') && orgSummary
            ? `Onboarded ${orgSummary.succeeded} ${mode === 'azure_mg' ? 'subscription(s)' : mode === 'gcp_org' ? 'project(s)' : 'account(s)'} (${orgSummary.failed} failed). Scans are running in the background.`
            : verifyError
              ? `Your ${provider.toUpperCase()} account was created, but its credentials did not verify.`
              : `Your ${provider.toUpperCase()} account is ready for scanning.`}
        </p>
        {/* The connector EXISTS — this is not a create failure — but a scan run
            against credentials that do not authenticate returns nothing, and an
            empty first-scan result reads as "your cloud is clean". Say so here
            rather than letting the next two steps imply otherwise. */}
        {verifyError && (
          <p className="mt-3 text-sm text-amber-400 max-w-md mx-auto">
            {verifyError} Fix it in Settings &rarr; Connectors before relying on the scan
            below — an unverified connector produces an empty result, not an error.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto w-full">
      <h2 className="text-2xl font-bold text-gray-200 mb-1">Connect your cloud account</h2>
      <p className="text-gray-400 mb-6">CyberShield uses read-only IAM roles. No write access, no agents required.</p>

      {/* Mode selector: single-account vs bulk-onboarding flows */}
      <div className="flex flex-wrap gap-2 mb-4">
        {([
          { value: 'single' as ConnectMode, label: 'Single Account' },
          { value: 'aws_org' as ConnectMode, label: 'AWS Organization (bulk)' },
          { value: 'azure_mg' as ConnectMode, label: 'Azure Management Group (bulk)' },
          { value: 'gcp_org' as ConnectMode, label: 'GCP Organization (bulk)' },
        ]).map(m => (
          <button
            key={m.value}
            onClick={() => { setMode(m.value); setError(''); }}
            className={`flex-1 py-2 rounded-lg border text-xs font-semibold transition-all ${
              mode === m.value
                ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400'
                : 'border-wiz-border bg-wiz-card text-gray-400 hover:border-gray-500'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Provider selector — hidden in AWS Org bulk mode */}
      {mode === 'single' && (
        <div className="flex gap-2 mb-6">
          {(Object.keys(PROVIDER_LABELS) as Provider[]).map(p => (
            <button
              key={p}
              onClick={() => {
                setProvider(p);
                setForm(seedCredForm(PROVIDER_CREDENTIAL_FORMS[p]));
                setError('');
                onProviderChange?.(p);
              }}
              className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition-all ${
                provider === p
                  ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400'
                  : 'border-wiz-border bg-wiz-card text-gray-400 hover:border-gray-500'
              }`}
            >
              {p.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {/* AWS Organization bulk-onboarding flow */}
      {mode === 'aws_org' && (
        <div className="space-y-4 mb-6">
          {orgAccounts.length === 0 ? (
            <>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Master account role ARN (Organizations:ListAccounts)</label>
                <input
                  type="text"
                  value={orgRoleArn}
                  onChange={e => setOrgRoleArn(e.target.value)}
                  placeholder="arn:aws:iam::111111111111:role/CyberShieldOrgReadOnly"
                  className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">
                    External ID
                    {/* G-UX-04 (2026-05-20): auto-fill button. The BE
                        `/api/v1/onboarding/aws/external-id` endpoint
                        generates a deterministic per-tenant XID and
                        also exposes a copy-friendly value. */}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const { data: eidData } = await apiClient.get<{ external_id: string }>('/api/v1/onboarding/aws/external-id');
                          if (eidData?.external_id) {
                            setOrgExternalId(eidData.external_id);
                            try { await navigator.clipboard.writeText(eidData.external_id); } catch {}
                          }
                        } catch {}
                      }}
                      className="ml-2 text-xs text-indigo-400 hover:text-indigo-300 underline"
                    >
                      Auto-generate + copy
                    </button>
                  </label>
                  <input
                    type="text"
                    value={orgExternalId}
                    onChange={e => setOrgExternalId(e.target.value)}
                    placeholder="csp-<tenant>-<random> (click 'Auto-generate' →)"
                    className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Member role name</label>
                  <input
                    type="text"
                    value={memberRoleName}
                    onChange={e => setMemberRoleName(e.target.value)}
                    placeholder="OrganizationAccountAccessRole"
                    className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                onClick={handleDiscoverOrg}
                disabled={orgDiscovering}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors"
              >
                {orgDiscovering ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {orgDiscovering ? 'Discovering org...' : 'Discover Organization'}
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-300">
                  Found <span className="text-indigo-400 font-semibold">{orgAccounts.length}</span> accounts &middot;{' '}
                  <span className="text-green-400">{orgSelected.size}</span> selected
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOrgSelected(new Set(orgAccounts.filter(a => a.status === 'ACTIVE').map(a => a.id)))}
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    Select active
                  </button>
                  <span className="text-gray-600 text-xs">|</span>
                  <button onClick={() => setOrgSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-300">
                    Clear
                  </button>
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto border border-wiz-border rounded-lg divide-y divide-theme-border">
                {orgAccounts.map(a => {
                  const checked = orgSelected.has(a.id);
                  const inactive = a.status && a.status !== 'ACTIVE';
                  return (
                    <button
                      key={a.id}
                      onClick={() => toggleOrgAccount(a.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs transition-colors ${
                        checked ? 'bg-indigo-500/5' : 'bg-transparent hover:bg-wiz-card'
                      }`}
                    >
                      <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        checked ? 'bg-indigo-600 border-indigo-600' : 'border-gray-600'
                      }`}>
                        {checked && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-200 font-medium truncate">{a.name || a.id}</p>
                        <p className="text-gray-500 truncate">
                          {a.id} &middot; {a.ou_path || '/'} {a.email ? `· ${a.email}` : ''}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        inactive ? 'bg-gray-700/40 text-gray-400' : 'bg-green-500/10 text-green-400'
                      }`}>
                        {a.status || 'UNKNOWN'}
                      </span>
                    </button>
                  );
                })}
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => { setOrgAccounts([]); setOrgSelected(new Set()); }}
                  className="px-4 py-2.5 border border-wiz-border hover:border-gray-500 text-gray-300 rounded-xl text-sm transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleOnboardOrg}
                  disabled={orgOnboarding || orgSelected.size === 0}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors"
                >
                  {orgOnboarding ? <Loader className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
                  {orgOnboarding ? 'Onboarding...' : `Onboard ${orgSelected.size} accounts`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* R220 — Azure Management Group bulk-onboard */}
      {mode === 'azure_mg' && (
        <div className="space-y-4 mb-6">
          {orgAccounts.length === 0 ? (
            <>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Management Group ID (root or any tenant MG)</label>
                <input type="text" value={azMgRoot} onChange={e => setAzMgRoot(e.target.value)}
                  placeholder="contoso-root"
                  className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">App registration client ID</label>
                  <input type="text" value={azClientId} onChange={e => setAzClientId(e.target.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 font-mono placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Tenant ID</label>
                  <input type="text" value={azTenantId} onChange={e => setAzTenantId(e.target.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 font-mono placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Client secret</label>
                <input type="password" value={azClientSecret} onChange={e => setAzClientSecret(e.target.value)}
                  className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 font-mono placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button onClick={handleDiscoverAzureMG} disabled={orgDiscovering}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-semibold">
                {orgDiscovering ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {orgDiscovering ? 'Discovering subscriptions…' : 'Discover subscriptions'}
              </button>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-400">Found <strong className="text-indigo-300">{orgAccounts.length}</strong> subscriptions. Select the ones to onboard.</p>
              <div className="bg-wiz-card border border-wiz-border rounded-lg max-h-64 overflow-auto">
                {orgAccounts.map(s => (
                  <label key={s.id} className="flex items-center gap-2 px-3 py-2 border-b border-wiz-border/40 last:border-b-0 hover:bg-wiz-card text-sm cursor-pointer">
                    <input type="checkbox" checked={orgSelected.has(s.id)} onChange={() => toggleOrgAccount(s.id)} />
                    <span className="text-white text-xs flex-1">{s.name || s.id}</span>
                    <span className="text-xs text-gray-500 font-mono">{s.id}</span>
                  </label>
                ))}
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-2">
                <button onClick={() => { setOrgAccounts([]); setOrgSelected(new Set()); }}
                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded text-xs">Back</button>
                <button onClick={handleOnboardAzureMG} disabled={orgOnboarding || orgSelected.size === 0}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-semibold">
                  {orgOnboarding ? <Loader className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
                  {orgOnboarding ? 'Onboarding…' : `Onboard ${orgSelected.size} subscription(s)`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* R220 — GCP Organization bulk-onboard */}
      {mode === 'gcp_org' && (
        <div className="space-y-4 mb-6">
          {orgAccounts.length === 0 ? (
            <>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Organization ID (numeric)</label>
                <input type="text" value={gcpOrgId} onChange={e => setGcpOrgId(e.target.value)}
                  placeholder="123456789012"
                  className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 font-mono placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Service account JSON key</label>
                <textarea value={gcpServiceAccountJson} onChange={e => setGcpServiceAccountJson(e.target.value)}
                  rows={6} placeholder='{"type":"service_account",...}'
                  className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2 text-xs text-gray-200 font-mono placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Folder filter (comma-sep folder IDs, blank = all)</label>
                <input type="text" value={gcpFolderFilter} onChange={e => setGcpFolderFilter(e.target.value)}
                  placeholder="folders/123, folders/456"
                  className="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 font-mono placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button onClick={handleDiscoverGCPOrg} disabled={orgDiscovering}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-semibold">
                {orgDiscovering ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {orgDiscovering ? 'Discovering projects…' : 'Discover projects'}
              </button>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-400">Found <strong className="text-indigo-300">{orgAccounts.length}</strong> projects. Select the ones to onboard.</p>
              <div className="bg-wiz-card border border-wiz-border rounded-lg max-h-64 overflow-auto">
                {orgAccounts.map(p => (
                  <label key={p.id} className="flex items-center gap-2 px-3 py-2 border-b border-wiz-border/40 last:border-b-0 hover:bg-wiz-card text-sm cursor-pointer">
                    <input type="checkbox" checked={orgSelected.has(p.id)} onChange={() => toggleOrgAccount(p.id)} />
                    <span className="text-white text-xs flex-1">{p.name || p.id}</span>
                    <span className="text-xs text-gray-500 font-mono">{p.id}</span>
                  </label>
                ))}
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-2">
                <button onClick={() => { setOrgAccounts([]); setOrgSelected(new Set()); }}
                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded text-xs">Back</button>
                <button onClick={handleOnboardGCPOrg} disabled={orgOnboarding || orgSelected.size === 0}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-semibold">
                  {orgOnboarding ? <Loader className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
                  {orgOnboarding ? 'Onboarding…' : `Onboard ${orgSelected.size} project(s)`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Single-account fields */}
      {mode === 'single' && (
        <>
          {PROVIDER_CREDENTIAL_FORMS[provider].note && (
            <p className="text-xs text-gray-500 mb-4">{PROVIDER_CREDENTIAL_FORMS[provider].note}</p>
          )}
          <div className="space-y-4 mb-6">
            <ProviderCredentialFields
              form={PROVIDER_CREDENTIAL_FORMS[provider]}
              values={form}
              onChange={(id, v) => { setForm(prev => ({ ...prev, [id]: v })); setError(''); }}
              inputClass="w-full bg-wiz-card border border-wiz-border rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 font-mono"
            />
          </div>

          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

          <button
            onClick={handleConnect}
            disabled={connecting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            {connecting ? <Loader className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
            {connecting ? 'Connecting...' : 'Connect Account'}
          </button>
        </>
      )}
    </div>
  );
};

// ── Step 2: Configure Scan ───────────────────────────────────────────────────
const AWS_REGIONS = ['us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 'ap-southeast-1', 'ap-northeast-1'];
const AZURE_REGIONS = ['eastus', 'westus2', 'westeurope', 'northeurope', 'southeastasia', 'australiaeast'];
const GCP_REGIONS = ['us-central1', 'us-east1', 'europe-west1', 'asia-east1', 'asia-southeast1'];

const REGION_MAP: Record<Provider, string[]> = { aws: AWS_REGIONS, azure: AZURE_REGIONS, gcp: GCP_REGIONS };

const StepConfigureScan: React.FC<{
  provider: Provider;
  selectedRegions: string[];
  scanType: ScanType;
  onRegionsChange: (regions: string[]) => void;
  onScanTypeChange: (type: ScanType) => void;
}> = ({ provider, selectedRegions, scanType, onRegionsChange, onScanTypeChange }) => {
  const regions = REGION_MAP[provider] || AWS_REGIONS;
  const toggleRegion = (r: string) => {
    onRegionsChange(
      selectedRegions.includes(r) ? selectedRegions.filter(x => x !== r) : [...selectedRegions, r]
    );
  };
  const selectAll = () => onRegionsChange([...regions]);
  const clearAll = () => onRegionsChange([]);

  return (
    <div className="max-w-xl mx-auto w-full">
      <h2 className="text-2xl font-bold text-gray-200 mb-1">Configure your scan</h2>
      <p className="text-gray-400 mb-6">Choose which regions to scan and the scan depth.</p>

      {/* Scan type */}
      <div className="mb-6">
        <label className="block text-xs text-gray-400 mb-2 font-medium">Scan Type</label>
        <div className="flex gap-3">
          {([
            { value: 'quick' as ScanType, label: 'Quick Scan', desc: 'Core checks only (~2 min)' },
            { value: 'full' as ScanType, label: 'Full Scan', desc: 'All rules + compliance (~10 min)' },
          ]).map(opt => (
            <button
              key={opt.value}
              onClick={() => onScanTypeChange(opt.value)}
              className={`flex-1 p-4 rounded-xl border text-left transition-all ${
                scanType === opt.value
                  ? 'border-indigo-500 bg-indigo-500/10'
                  : 'border-wiz-border bg-wiz-card hover:border-gray-500'
              }`}
            >
              <p className={`text-sm font-semibold ${scanType === opt.value ? 'text-indigo-400' : 'text-gray-300'}`}>{opt.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Regions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-400 font-medium">Regions ({selectedRegions.length}/{regions.length} selected)</label>
          <div className="flex gap-2">
            <button onClick={selectAll} className="text-xs text-indigo-400 hover:text-indigo-300">Select All</button>
            <span className="text-gray-600 text-xs">|</span>
            <button onClick={clearAll} className="text-xs text-gray-500 hover:text-gray-300">Clear</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
          {regions.map(r => (
            <button
              key={r}
              onClick={() => toggleRegion(r)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all ${
                selectedRegions.includes(r)
                  ? 'border-indigo-500/50 bg-indigo-500/10 text-indigo-400'
                  : 'border-wiz-border bg-wiz-card text-gray-400 hover:border-gray-500'
              }`}
            >
              <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                selectedRegions.includes(r) ? 'bg-indigo-600 border-indigo-600' : 'border-gray-600'
              }`}>
                {selectedRegions.includes(r) && <CheckCircle className="w-2.5 h-2.5 text-white" />}
              </div>
              {r}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Step 3: First Scan ───────────────────────────────────────────────────────
const StepFirstScan: React.FC<{
  accountId: string | null;
  scanId: string | null;
  scanStatus: string;
  scanProgress: number;
  onTriggerScan: () => void;
}> = ({ accountId, scanId, scanStatus, scanProgress, onTriggerScan }) => (
  <div className="max-w-xl mx-auto text-center">
    <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mx-auto mb-6">
      <Search className="w-8 h-8 text-indigo-400" />
    </div>
    <h2 className="text-2xl font-bold text-gray-200 mb-2">Run your first security scan</h2>
    <p className="text-gray-400 mb-8">
      {!scanId
        ? 'Scan your cloud environment to discover resources and detect misconfigurations.'
        : scanStatus === 'completed'
          ? 'Scan complete! Proceed to see your results.'
          : scanStatus === 'failed'
            ? 'Scan encountered an error. You can try again or proceed with available data.'
            : scanStatus === 'partial'
              ? 'The scan finished, but some checks could not run — so this is not a clean bill of health. Proceed to see what was and was not assessed.'
              : scanStatus === 'cancelled'
                ? 'The scan was cancelled before it finished. Proceed to see partial results, or run it again.'
                : 'Scanning your cloud environment...'}
    </p>

    {!scanId && (
      <button
        onClick={onTriggerScan}
        disabled={!accountId}
        className="inline-flex items-center gap-2 px-8 py-3.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-xl text-base font-semibold transition-all shadow-lg shadow-indigo-500/20"
      >
        <Search className="w-5 h-5" />
        Start Scan
      </button>
    )}

    {scanId && !isTerminalScan(scanStatus) && (
      <div className="max-w-sm mx-auto">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-400">Scanning...</span>
          <span className="text-indigo-400 font-medium">{scanProgress}%</span>
        </div>
        <div className="w-full h-2 bg-wiz-card rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-teal-500 rounded-full transition-all duration-500"
            style={{ width: `${scanProgress}%` }}
          />
        </div>
        <p className="text-gray-500 text-xs mt-3">This may take a few minutes depending on your environment size.</p>
      </div>
    )}

    {scanStatus === 'completed' && (
      <div className="flex items-center justify-center gap-2 text-green-400">
        <CheckCircle className="w-5 h-5" />
        <span className="font-medium">Scan completed successfully</span>
      </div>
    )}

    {/* AMBER, NOT GREEN, AND NOT RED. Anything that reached a terminal state
        without assessing everything gets its own line — the green tick above is
        reserved for a scan that actually looked. */}
    {(scanStatus === 'partial' || scanStatus === 'cancelled') && (
      <div className="flex items-center justify-center gap-2 text-amber-400">
        <AlertTriangle className="w-5 h-5" />
        <span className="font-medium">
          {scanStatus === 'partial'
            ? 'Scan finished — not everything could be assessed'
            : 'Scan cancelled before it finished'}
        </span>
      </div>
    )}

    {scanStatus === 'failed' && (
      <div className="space-y-3">
        <p className="text-red-400 text-sm">Scan failed. You can retry or proceed with demo data.</p>
        <button
          onClick={onTriggerScan}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-wiz-card border border-wiz-border hover:border-gray-500 text-gray-300 rounded-xl text-sm transition-all"
        >
          Retry Scan
        </button>
      </div>
    )}

    {!accountId && (
      <p className="text-gray-500 text-sm mt-4">
        No cloud account connected. Go back to connect one, or proceed with demo data.
      </p>
    )}
  </div>
);

// ── Step 3: Results ──────────────────────────────────────────────────────────
const StepResults: React.FC<{
  summary: { resources: number; findings: number; riskScore: number } | null;
  loading: boolean;
  onGoToDashboard: () => void;
}> = ({ summary, loading, onGoToDashboard }) => (
  <div className="max-w-xl mx-auto text-center">
    <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
      <CheckCircle className="w-8 h-8 text-green-400" />
    </div>
    <h2 className="text-2xl font-bold text-gray-200 mb-2">Your security posture</h2>
    <p className="text-gray-400 mb-8">Here is a summary of what we found in your cloud environment.</p>

    {loading ? (
      <div className="flex items-center justify-center gap-3 py-8">
        <Loader className="w-5 h-5 text-indigo-400 animate-spin" />
        <span className="text-gray-400">Loading results...</span>
      </div>
    ) : (
      <>
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-wiz-card border border-wiz-border rounded-xl p-5">
            <div className="text-3xl font-bold text-gray-200 mb-1">{summary?.resources ?? 0}</div>
            <div className="text-xs text-gray-500 font-medium">Resources Found</div>
          </div>
          <div className="bg-wiz-card border border-wiz-border rounded-xl p-5">
            <div className="text-3xl font-bold text-orange-400 mb-1">{summary?.findings ?? 0}</div>
            <div className="text-xs text-gray-500 font-medium">Findings</div>
          </div>
          <div className="bg-wiz-card border border-wiz-border rounded-xl p-5">
            <div className={`text-3xl font-bold mb-1 ${
              (summary?.riskScore ?? 0) >= 80 ? 'text-green-400' : (summary?.riskScore ?? 0) >= 50 ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {summary?.riskScore ?? 0}
            </div>
            <div className="text-xs text-gray-500 font-medium">Risk Score</div>
          </div>
        </div>

        <button
          onClick={onGoToDashboard}
          className="inline-flex items-center gap-3 px-8 py-3.5 bg-gradient-to-r from-indigo-600 to-teal-600 hover:from-indigo-500 hover:to-teal-500 text-white rounded-xl text-base font-semibold transition-all shadow-lg shadow-indigo-500/25"
        >
          Go to Dashboard
          <ArrowRight className="w-5 h-5" />
        </button>
      </>
    )}
  </div>
);

// ── Main Wizard ──────────────────────────────────────────────────────────────
//
// R188-D 2026-05-19: Stage-24 Customer Onboarding P1 fix #3 — telemetry.
//   Previously this component wrote ONLY to localStorage in handleComplete.
//   `time_to_first_issue` / `onboarding_progress` / completion-rate were
//   unmeasured fleet-wide. Now every milestone POSTs to
//   /api/v1/admin/onboarding/event so the customer-success dashboard
//   (OnboardingProgressPanel) can compute Wiz-parity KPIs.
//   localStorage retained as offline-UX fallback cache.
const ONBOARDING_TELEMETRY_PATH = '/api/v1/admin/onboarding/event';
const postOnboardingEvent = async (
  event_type: string,
  startedAt: number | null,
  extra?: Record<string, unknown>,
): Promise<void> => {
  try {
    const duration_ms =
      startedAt != null ? Math.max(0, Date.now() - startedAt) : null;
    await apiClient.post(ONBOARDING_TELEMETRY_PATH, {
      event_type,
      step_name: (extra as { step_name?: string } | undefined)?.step_name,
      duration_ms,
      metadata: extra ?? {},
    });
  } catch {
    // Telemetry must never break the wizard. Best-effort only.
  }
};

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider>('aws');
  const [selectedRegions, setSelectedRegions] = useState<string[]>(['us-east-1']);
  const [scanType, setScanType] = useState<ScanType>('quick');
  const [scanId, setScanId] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState('idle');
  const [scanProgress, setScanProgress] = useState(0);
  const [summary, setSummary] = useState<{ resources: number; findings: number; riskScore: number } | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  // R188-D: wizard-start timestamp pinned at first render so duration_ms is
  // computed relative to wizard_started for every later event.
  const [wizardStartedAt] = useState<number>(() => Date.now());

  // R188-D: fire wizard_started on mount + emit one wizard_step_completed per
  // step transition. Skip/complete fire in the handlers below.
  useEffect(() => {
    void postOnboardingEvent('wizard_started', null, { initial_step: 0 });
    // Cache locally so the wizard can resume after a refresh
    try {
      localStorage.setItem('onboarding_started_at', String(wizardStartedAt));
    } catch { /* ignore quota */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // R188-D: emit first_cloud_connected once an accountId lands.
  useEffect(() => {
    if (accountId) {
      void postOnboardingEvent('first_cloud_connected', wizardStartedAt, {
        provider: selectedProvider,
        account_id: accountId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // R188-D: emit first_scan_started once a scanId lands.
  useEffect(() => {
    if (scanId) {
      void postOnboardingEvent('first_scan_started', wizardStartedAt, {
        scan_id: scanId,
        scan_type: scanType,
        region_count: selectedRegions.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanId]);

  // R188-D: emit first_issue_observed once summary.findings > 0 lands.
  useEffect(() => {
    if (summary && summary.findings > 0) {
      void postOnboardingEvent('first_issue_observed', wizardStartedAt, {
        findings: summary.findings,
        resources: summary.resources,
        risk_score: summary.riskScore,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary?.findings]);

  // Trigger scan
  const handleTriggerScan = async () => {
    setScanStatus('running');
    setScanProgress(10);
    try {
      const { data, error } = await apiClient.post<any>('/api/v1/scans', { account_id: accountId, scan_type: scanType, regions: selectedRegions });
      if (!error && data) {
        setScanId((data as any).id || (data as any).scan_id || 'scan-1');
      } else {
        // Fallback: try the cspm scan endpoint
        const { data: data2, error: error2 } = await apiClient.post<any>('/api/v1/cspm/scan', { account_ids: accountId ? [accountId] : [] });
        if (!error2 && data2) {
          setScanId((data2 as any).id || (data2 as any).scan_id || 'scan-1');
        } else {
          setScanId('scan-fallback');
        }
      }
    } catch {
      setScanId('scan-fallback');
    }
  };

  // Poll scan status
  useEffect(() => {
    if (!scanId || isTerminalScan(scanStatus)) return;

    let cancelled = false;
    // Honest status: never fabricate progress/completion. GET /api/v1/scans/{id}
    // is a real endpoint; if it keeps failing we surface 'failed', we do NOT
    // fake a successful first scan.
    let failures = 0;
    const MAX_FAILURES = 5; // ~15s at the 3s poll interval
    const poll = async () => {
      try {
        const { data, error } = await apiClient.get<any>(`/api/v1/scans/${scanId}`);
        if (!error && data) {
          failures = 0;
          if (!cancelled) {
            const d = data as any;
            // GET /api/v1/scans/{id} emits `progress_percent`; neither
            // `progress` nor `percentage` is a key of that payload, so `pct`
            // always fell through to the current state and the bar sat frozen
            // at the 25% initial bump until status flipped to completed.
            // Curl 2026-07-31: keys = scan_id, status, step, progress_percent,
            // resource_count, finding_count, started_at, completed_at, error,
            // total_regions, completed_regions, current_region, region_details,
            // pipeline_steps, errors, ...
            const pct = d.progress_percent ?? d.progress ?? d.percentage ?? scanProgress;
            setScanProgress(Math.min(pct, 100));
            if (d.status === 'completed' || d.status === 'done') {
              setScanStatus('completed');
              setScanProgress(100);
            } else if (d.status === 'failed' || d.status === 'error') {
              setScanStatus('failed');
            } else if (d.status === 'partial' || d.status === 'cancelled') {
              // A THIRD OUTCOME, KEPT SEPARATE FROM BOTH. Mapping `partial` to
              // `completed` would put "Scan completed successfully" under a
              // green tick on a scan that inspected nothing — on the user's
              // very first screen, which is the worst place this platform could
              // tell that particular lie. Mapping it to `failed` is the other
              // error: the scan ran, and whatever it did reach is real.
              setScanStatus(d.status);
              setScanProgress(100);   // it is over; the bar must stop moving
            }
          }
        } else {
          failures += 1;
          if (!cancelled && failures >= MAX_FAILURES) setScanStatus('failed');
        }
      } catch {
        failures += 1;
        if (!cancelled && failures >= MAX_FAILURES) setScanStatus('failed');
      }
    };

    const interval = setInterval(poll, 3000);
    // Initial bump
    setTimeout(() => { if (!cancelled) setScanProgress(p => Math.max(p, 25)); }, 1000);

    return () => { cancelled = true; clearInterval(interval); };
  }, [scanId, scanStatus, scanProgress]);

  // Fetch summary when entering step 3
  useEffect(() => {
    if (currentStep !== 4 || summary) return;
    setSummaryLoading(true);
    (async () => {
      try {
        const { data, error } = await apiClient.get<any>('/api/v1/dashboard/summary');
        if (!error && data) {
          const d = data as any;
          // GET /api/v1/dashboard/summary emits total_assets / total_findings /
          // compliance_score. `total_resources`, `resource_count`,
          // `security_score`, `risk_score` and `overall_score` are NOT keys of
          // this payload, so the wizard's final screen reported "0 resources,
          // risk score 0" over a live estate. Curl 2026-07-31: keys =
          // total_assets(2326), total_findings(30666), critical_findings,
          // high_findings, compliance_score(34.8), attack_paths,
          // toxic_combinations, identities_at_risk, data_stores_at_risk,
          // vulnerabilities_critical. Only total_findings ever resolved.
          setSummary({
            resources: d.total_assets ?? d.total_resources ?? d.resource_count ?? 0,
            findings: d.total_findings ?? d.finding_count ?? d.open_findings ?? 0,
            riskScore: d.compliance_score ?? d.security_score ?? d.risk_score ?? d.overall_score ?? 0,
          });
        } else {
          setSummary({ resources: 0, findings: 0, riskScore: 0 });
        }
      } catch {
        setSummary({ resources: 0, findings: 0, riskScore: 0 });
      } finally {
        setSummaryLoading(false);
      }
    })();
  }, [currentStep, summary]);

  const handleComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    // Also set the legacy key for backward compat
    localStorage.setItem('onboarding_complete', 'true');
    // R188-D: fire wizard_completed before invoking onComplete so the
    // telemetry post races on its own promise — onComplete navigates away.
    void postOnboardingEvent('wizard_completed', wizardStartedAt, {
      final_step: currentStep,
      account_id: accountId,
      scan_id: scanId,
      findings: summary?.findings ?? 0,
    });
    onComplete();
  };

  const next = () => {
    if (currentStep < 4) {
      const nextStep = currentStep + 1;
      // R188-D: emit wizard_step_completed for the step we're leaving.
      void postOnboardingEvent('wizard_step_completed', wizardStartedAt, {
        step_name: STEP_LABELS[currentStep],
        completed_step: currentStep,
        next_step: nextStep,
      });
      setCurrentStep(nextStep);
    }
  };
  const back = () => { if (currentStep > 0) setCurrentStep(s => s - 1); };
  const skip = () => {
    // R188-D: distinguish skipped (manual abort) from completed (full run).
    void postOnboardingEvent('wizard_skipped', wizardStartedAt, {
      abandoned_at_step: currentStep,
      step_name: STEP_LABELS[currentStep],
    });
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.setItem('onboarding_complete', 'true');
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-wiz-darker flex flex-col z-50">
      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-theme-border shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-teal-600 flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <span className="text-gray-200 font-semibold text-sm">CyberShield</span>
          <span className="text-gray-600 text-sm mx-1">/</span>
          <span className="text-gray-400 text-sm">Setup Wizard</span>
        </div>
        <button onClick={skip} className="text-gray-500 hover:text-gray-300 text-sm transition-colors">
          Skip setup
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-center gap-0 px-8 py-5 shrink-0">
        {STEP_LABELS.map((label, i) => {
          const Icon = STEP_ICONS[i];
          return (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  i < currentStep
                    ? 'bg-indigo-600 text-white'
                    : i === currentStep
                      ? 'border-2 border-indigo-500 text-indigo-400'
                      : 'border-2 border-wiz-border text-gray-600'
                }`}>
                  {i < currentStep ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${
                  i === currentStep ? 'text-gray-200' : i < currentStep ? 'text-indigo-400' : 'text-gray-600'
                }`}>
                  {label}
                </span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div className={`h-0.5 w-20 mb-5 mx-2 transition-all ${i < currentStep ? 'bg-indigo-600' : 'bg-theme-elevated'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-8 py-6 flex items-start justify-center">
        <div className="w-full pt-4">
          {currentStep === 0 && <StepWelcome />}
          {currentStep === 1 && (
            <StepConnectCloud accountId={accountId} onAccountCreated={(id) => { setAccountId(id); }} onProviderChange={setSelectedProvider} />
          )}
          {currentStep === 2 && (
            <StepConfigureScan
              provider={selectedProvider}
              selectedRegions={selectedRegions}
              scanType={scanType}
              onRegionsChange={setSelectedRegions}
              onScanTypeChange={setScanType}
            />
          )}
          {currentStep === 3 && (
            <StepFirstScan
              accountId={accountId}
              scanId={scanId}
              scanStatus={scanStatus}
              scanProgress={scanProgress}
              onTriggerScan={handleTriggerScan}
            />
          )}
          {currentStep === 4 && (
            <StepResults summary={summary} loading={summaryLoading} onGoToDashboard={handleComplete} />
          )}
        </div>
      </div>

      {/* Footer nav */}
      <div className="flex items-center justify-between px-8 py-4 border-t border-theme-border shrink-0">
        <button
          onClick={back}
          disabled={currentStep === 0}
          className="flex items-center gap-1.5 px-5 py-2.5 text-gray-400 hover:text-gray-200 disabled:opacity-30 transition-colors text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        {currentStep < 4 ? (
          <button
            onClick={next}
            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {currentStep === 3 && scanStatus === 'running' ? 'Skip scan' : 'Next'}
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleComplete}
            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Go to Dashboard
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;
